import { Link, router } from "@inertiajs/react"
import { Filter, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type {
  IssueSeverity,
  IssueType,
  QualityFilters,
  QualityUrls,
} from "@/lib/types"

interface Props {
  filters: QualityFilters
  urls: QualityUrls
  totalIssues: number
}

const ISSUE_TYPE_LABELS: Record<IssueType, string> = {
  stale_poi: "Stale POI",
  weak_metadata: "Weak metadata",
  coverage_gap: "Coverage gap",
  duplicate: "Duplicate",
  hotel_gap: "Hotel gap",
}

const SEVERITY_LABELS: Record<IssueSeverity, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
}

// Stable sentinel — shadcn Select forbids `value=""` as an option, so we use
// a placeholder string and map to `null` at change time.
const ANY = "__any__"

function updateFilter(
  urls: QualityUrls,
  current: QualityFilters,
  key: "issue_type" | "severity",
  value: string | null,
) {
  const next: Record<string, string> = {}
  if (key === "issue_type") {
    if (value) next.issue_type = value
    if (current.severity) next.severity = current.severity
  } else {
    if (current.issue_type) next.issue_type = current.issue_type
    if (value) next.severity = value
  }
  router.visit(urls.quality, { data: next, preserveScroll: true })
}

export function FilterBar({ filters, urls, totalIssues }: Props) {
  const hasFilters = Boolean(filters.issue_type || filters.severity)

  return (
    <div
      className="sticky top-0 z-10 -mx-2 flex flex-wrap items-center gap-2 rounded-md border bg-background/95 px-2 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      role="region"
      aria-label="Issue filters"
    >
      <Filter className="size-4 text-muted-foreground" aria-hidden />
      <span className="text-xs text-muted-foreground">Filters:</span>

      <Select
        value={filters.issue_type ?? ANY}
        onValueChange={(v) => updateFilter(urls, filters, "issue_type", v === ANY ? null : v)}
      >
        <SelectTrigger className="h-8 w-44 text-xs" aria-label="Filter by issue type">
          <SelectValue placeholder="Any type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any type</SelectItem>
          {(Object.keys(ISSUE_TYPE_LABELS) as IssueType[]).map((t) => (
            <SelectItem key={t} value={t}>
              {ISSUE_TYPE_LABELS[t]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.severity ?? ANY}
        onValueChange={(v) => updateFilter(urls, filters, "severity", v === ANY ? null : v)}
      >
        <SelectTrigger className="h-8 w-36 text-xs" aria-label="Filter by severity">
          <SelectValue placeholder="Any severity" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any severity</SelectItem>
          {(Object.keys(SEVERITY_LABELS) as IssueSeverity[]).map((s) => (
            <SelectItem key={s} value={s}>
              {SEVERITY_LABELS[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasFilters && (
        <Button asChild variant="ghost" size="sm" className="h-8 text-xs">
          <Link href={urls.quality} preserveScroll aria-label="Clear all filters">
            <X className="mr-1 size-3.5" aria-hidden />
            Clear
          </Link>
        </Button>
      )}

      <span className="ml-auto text-xs text-muted-foreground tabular-nums">
        {totalIssues} {totalIssues === 1 ? "issue" : "issues"}
      </span>
    </div>
  )
}
